import { configureTracker, ErrorCodes, ErrorTypes, TrackerClients } from '@klarna-web-sdk/utils'
import { SDKConfig, ShoppingRuntimeConfig } from '@klarna-web-sdk/utils/src/types'
import { v4 as uuid } from 'uuid'

import { sessionListenerFunc, ShoppingSession } from './types'
import { handleError } from './utils/errorHandlers'
import { ShoppingSessionBridgeStorage } from './utils/shoppingSessionBridgeStorage'
import {
  createShoppingSession,
  fetchInteroperabilityToken,
  getShoppingSession,
  getShoppingSessionBySdkToken,
} from './utils/shoppingSessionRequests'

type TModules = keyof ShoppingRuntimeConfig['rolloutWeights']

enum TrackingEvents {
  SESSION_RETRIEVED = 'session_retrieved',
  SESSION_RESET = 'session_reset',
  INTEROPERABILITY_TOKEN_FETCHED = 'interoperability_token_fetched',
  SESSION_CREATED = 'session_created',
  SESSION_CREATED_ON_INTERACTION = 'session_created_on_interaction',
  SESSION_RESUMED = 'session_resumed',
  SESSION_RESUMED_BY_SDK_TOKEN = 'session_resumed_by_sdk_token',
  SESSION_SUBMITTED = 'session_submitted',
  SESSION_ID_REQUESTED = 'session_id_requested',
  DEPRECATED_SESSION_ID_REQUESTED = 'deprecated_session_id_requested',
}

export class ShoppingSDK {
  // eslint-disable-next-line no-use-before-define
  private static instance: ShoppingSDK
  public static getInstance(sdkConfig?: SDKConfig) {
    if (ShoppingSDK.instance) {
      return ShoppingSDK.instance
    } else if (sdkConfig) {
      ShoppingSDK.instance = new ShoppingSDK(sdkConfig)
      return ShoppingSDK.instance
    } else {
      return undefined
    }
  }

  private shoppingSessionId: string | undefined
  private shoppingSession: ShoppingSession | undefined
  private listeners: Record<string, sessionListenerFunc> = {}
  private sdkConfig: SDKConfig
  private tracker: ReturnType<typeof configureTracker>
  private storage: ShoppingSessionBridgeStorage

  constructor(sdkConfig: SDKConfig) {
    this.sdkConfig = sdkConfig
    this.storage = new ShoppingSessionBridgeStorage()
    this.tracker = configureTracker({
      config: sdkConfig,
      trackerClient: TrackerClients.websdk,
      extraTrackingData: {},
    })

    // set instance to be used globally
    ShoppingSDK.instance = this
  }

  private async resetSession() {
    this.shoppingSession = undefined
    this.shoppingSessionId = undefined
    await this.storage.removeSessionId()
    this.triggerListeners()
    this.tracker.info(TrackingEvents.SESSION_RESET, {
      shoppingSessionId: this.shoppingSessionId,
    })
  }

  private async retrieveSessionFromService(shoppingSessionId: string) {
    try {
      this.shoppingSession = await getShoppingSession(shoppingSessionId)
      this.tracker.info(TrackingEvents.SESSION_RETRIEVED, {
        shoppingSessionId: this.shoppingSessionId,
      })
      this.triggerListeners()
    } catch (error) {
      this.resetSession()
      handleError({
        type: ErrorTypes.TECHNICAL_ERROR,
        code: ErrorCodes.INTERNAL_ERROR,
        message: 'Shopping Session not found for provided shoppingSessionId',
        originalError: error,
      })
    }
  }

  private async createSessionInService() {
    try {
      this.shoppingSession = await createShoppingSession()
      this.shoppingSessionId = this.shoppingSession.shoppingSessionId
      await this.storage.setSessionId(this.shoppingSessionId)
      this.tracker.info(TrackingEvents.SESSION_CREATED, {
        shoppingSessionId: this.shoppingSessionId,
      })
      return this.shoppingSessionId
    } catch (error) {
      handleError({
        type: ErrorTypes.TECHNICAL_ERROR,
        code: ErrorCodes.INTERNAL_ERROR,
        message: 'Error creating shopping session',
        originalError: error,
      })
      return undefined
    }
  }

  private triggerListeners() {
    Object.values(this.listeners).forEach((listener) => {
      listener(this.shoppingSession)
    })
  }

  /**
   * This method will check if a session is available in Partner local-storage.
   * If available, it will read the session id from backend-bridge local-storage.
   * Afterward fetch the session from the service using backend-bridge.
   */
  public async initializeSession() {
    if (this.storage?.isSessionAvailable()) {
      const sessionId = await this.storage.getSessionId()
      if (sessionId) {
        this.shoppingSessionId = sessionId
        await this.retrieveSessionFromService(sessionId)
      }
    }
  }

  public async getInteroperabilityToken(): Promise<string | undefined> {
    try {
      let shoppingSessionId = this.shoppingSessionId

      if (!shoppingSessionId) shoppingSessionId = await this.createSessionInService()
      if (!shoppingSessionId) return undefined

      const { interoperabilityToken } = await fetchInteroperabilityToken(shoppingSessionId)
      this.tracker.info(TrackingEvents.INTEROPERABILITY_TOKEN_FETCHED, {
        shoppingSessionId,
        interoperabilityToken,
      })
      return interoperabilityToken
    } catch (error) {
      handleError({
        type: ErrorTypes.TECHNICAL_ERROR,
        code: ErrorCodes.INTERNAL_ERROR,
        message: 'Error while fetching interoperability token',
        originalError: error,
      })
      return undefined
    }
  }

  public async deprecated_GetShoppingSessionId(
    {
      forceCreateSession,
    }: {
      forceCreateSession: boolean
    } = { forceCreateSession: false }
  ): Promise<string | undefined> {
    this.tracker.info(TrackingEvents.DEPRECATED_SESSION_ID_REQUESTED, {
      forceCreateSession,
      hasExistingSession: !!this.shoppingSessionId,
    })

    if (this.shoppingSessionId) return this.shoppingSessionId
    else {
      if (!forceCreateSession) return undefined

      try {
        await this.createSessionInService()
        return this.shoppingSessionId
      } catch (error) {
        handleError({
          type: ErrorTypes.TECHNICAL_ERROR,
          code: ErrorCodes.INTERNAL_ERROR,
          message: 'Error while fetching shopping session id',
          originalError: error,
        })
        return undefined
      }
    }
  }

  public addListener(listener: sessionListenerFunc) {
    const listenerId = uuid()
    this.listeners[listenerId] = listener
    return listenerId
  }

  public removeListener(listenerId: string) {
    delete this.listeners[listenerId]
  }

  public async refresh() {
    if (!this.shoppingSessionId) return
    await this.retrieveSessionFromService(this.shoppingSessionId)
  }

  public async userInteracted() {
    if (!this.shoppingSessionId) {
      try {
        await this.createSessionInService()
        this.triggerListeners()
        this.tracker.info(TrackingEvents.SESSION_CREATED_ON_INTERACTION, {
          shoppingSessionId: this.shoppingSessionId,
        })
      } catch (error) {
        handleError({
          type: ErrorTypes.TECHNICAL_ERROR,
          code: ErrorCodes.INTERNAL_ERROR,
          message: 'Error creating shopping session',
          originalError: error,
        })
      }
    }
  }

  public async resumeSessionWithSdkToken(sdkToken: string) {
    try {
      this.shoppingSession = await getShoppingSessionBySdkToken(sdkToken)
      this.shoppingSessionId = this.shoppingSession?.shoppingSessionId
      await this.storage.setSessionId(this.shoppingSessionId)
      this.tracker.info(TrackingEvents.SESSION_RESUMED_BY_SDK_TOKEN, {
        shoppingSessionId: this.shoppingSessionId,
      })
      this.triggerListeners()
    } catch (error) {
      this.resetSession()
      handleError({
        type: ErrorTypes.TECHNICAL_ERROR,
        code: ErrorCodes.INTERNAL_ERROR,
        message: 'Error while resuming shopping session with sdk token',
        originalError: error,
      })
    }
  }

  public getRolloutWeight(module: TModules): number {
    const rolloutWeight = this.sdkConfig.runtimeConfig?.shopping?.rolloutWeights?.[module]
    if (rolloutWeight !== undefined) return rolloutWeight

    handleError({
      type: ErrorTypes.TECHNICAL_ERROR,
      code: ErrorCodes.INTERNAL_ERROR,
      message: `Shopping session rollout weights not found in runtime config for module - ${module}`,
    })

    return 0
  }
}

export const useShoppingSession = (config: {
  module: TModules
}): Pick<ShoppingSDK, keyof ShoppingSDK> => {
  const ShoppingSdkInstance = ShoppingSDK.getInstance()
  if (!ShoppingSdkInstance) {
    throw new Error(
      'Shopping SDK is not initialized. Ensure your package is initialized after initialization of Shopping SDK in the main SDK constructor.'
    )
  }

  const rolloutWeight = ShoppingSdkInstance.getRolloutWeight(config.module)

  if (Math.random() < rolloutWeight) {
    return new Proxy(ShoppingSdkInstance, {
      get(target, prop: keyof ShoppingSDK) {
        const value = target[prop]
        return typeof value === 'function' ? value.bind(target) : value
      },
    })
  } else {
    return {
      deprecated_GetShoppingSessionId: async (): Promise<string | undefined> => {
        return undefined
      },
      addListener: () => {
        return 'fakeListenerId'
      },
      removeListener: () => {},
      userInteracted: async () => {},
      refresh: async () => {},
      getInteroperabilityToken: async (): Promise<string | undefined> => {
        return undefined
      },
      resumeSessionWithSdkToken: async () => {},
      getRolloutWeight: () => 0,
      initializeSession: async () => {},
    }
  }
}
